import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Box,
  Modal,
  FormHelperText,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import AccessibleTooltip from "../Tooltip/Tooltip.js";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import closeIcon from "../../assets/images/closeicon.svg";
import downloadFile from "../../assets/images/downloadFile.svg";
import uploadFileImg from "../../assets/images/uploadFile.svg";

import { field } from "./FieldJson";
import checkImg from "../../assets/images/check_circle.svg";
import { jwtDecode } from "jwt-decode";
import PropTypes from "prop-types";
import ComboBox from "../ComboBox";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDeviceTypes,
  fetchStatusTypes,
  addDevice,
  updateDevice,
  fetchTriggerOptions,
  resetDeviceState,
  uploadActions,
} from "../../Actions/AddEditDeviceAction";
import { subscribeMQTT } from "../../Actions/mqttActions.js";
import ComboBoxMultiSelect from "../ComboBoxMultiSelect";
import "./style.scss";

import {
  getInitialFormData,
  validateDeviceForm,
  getEditFormDataFromEditData,
  buildAddDevicePayload,
  buildUpdateDevicePayload,
  downloadErrorCSV,
  getConnectionClass,
  getFormattedValue,
} from "../../utils/addEditDevice.js";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  paddingLeft: 4,
  paddingRight: 4,
};

const AddEditDevice = (props) => {
  const [formData, setFormData] = useState(getInitialFormData());
  const [checked, setChecked] = useState(false);
  const [renderDeviceType, setRenderDeviceType] = useState({});
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [successMessage, setSuccessMessage] = useState(false);
  const [testConnectionStatus, setTestConnectionStatus] = useState("");
  const [streamUrlNew, setStreamUrlNew] = useState("");
  const [selectedNdevice, setSelectedNdevice] = useState("");
  const [mapWithNiagraList, setMapWithNiagraList] = useState([]);

  const {
    setOpen,
    open,
    actionButton,
    setActionButton,
    editData,
    existingDeviceNames = [],
    handleClose: handleCloseFromParent,
    setFormError,
    formError,
  } = props;

  const mqtt = useSelector((state) => state.mqtt);
  const messages = mqtt.messages;
  const dispatch = useDispatch();
  const deviceTypeState = useSelector(
    (state) => state.addEditDevice.deviceTypes
  );
  const statusTypeState = useSelector(
    (state) => state.addEditDevice.statusTypes
  );
  const duplicateError = useSelector(
    (state) => state.addEditDevice.duplicatErr.duplicateError
  );
  const triggerOptionsState = useSelector(
    (state) => state.addEditDevice.triggerOptions
  );
  const addDeviceState = useSelector((state) => state.addEditDevice.addDevice);
  const updateDeviceState = useSelector(
    (state) => state.addEditDevice.updateDevice
  );
  const bulkupload = useSelector((state) => state.addEditDevice.bulkupload);
  const bulkuploadListFail = useSelector(
    (state) => state.addEditDevice.errorSheet
  );

  const deviceType = deviceTypeState.data || [];
  const statusType = statusTypeState.data || [];
  const triggerOption = triggerOptionsState.data || [];

  const firstInputRefClose = useRef(null);
  const downloadErrorBtn = useRef(null);

  useEffect(() => {
    if (successMessage) {
      if (open && firstInputRefClose.current) {
        firstInputRefClose.current.focus();
      }
    }
  }, [successMessage, open]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (open && firstInputRefClose.current) {
        firstInputRefClose.current.focus();
      }
    }, 200);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [open]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        bulkuploadListFail?.errorFile?.length > 0 &&
        downloadErrorBtn.current
      ) {
        downloadErrorBtn.current.focus();
      }
    }, 200);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [bulkuploadListFail]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "streamUrl") {
      setStreamUrlNew(value);
    }
  };

  const handleDeviceTypeChange = (name) => {
    const selectedType = deviceType.find((type) => type.name === name);
    setRenderDeviceType(selectedType || {});
    setFormData((prevState) => ({
      ...prevState,
      type: selectedType || {},
    }));
  };

  const handleStatusTypeChange = (e) => {
    const selectedStatus = statusType.find(
      (status) => status.id === Number(e.target.value)
    );
    setFormData((prevState) => ({
      ...prevState,
      status: selectedStatus ? selectedStatus.id : 0,
    }));
  };

  const handleDateChange = (newValue) => {
    setFormData((prevState) => ({
      ...prevState,
      dateInstalled: newValue ? dayjs(newValue) : null,
    }));
  };

  const handleTriggersId = (value) => {
    setFormData((prevState) => ({
      ...prevState,
      triggers: value,
    }));
  };

  const runValidation = () => {
    const { isValid, errors } = validateDeviceForm({
      formData,
      actionButton,
      existingDeviceNames,
    });
    setFormError(errors);
    return isValid;
  };

  useEffect(() => {
    if (duplicateError) {
      setFormError((prev) => ({
        ...prev,
        serialNumber: duplicateError,
      }));
    }
  }, [duplicateError, setFormError]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const decodedToken = jwtDecode(token);
      const formDataToSend = buildAddDevicePayload(
        formData,
        decodedToken.userGuid
      );
      dispatch(addDevice(formDataToSend));
      dispatch({ type: "DEVICE_DUPLICATE_RESET" });
    } catch (error) {
      // console.error("Error making request:", error);
    }
  };

  const resetLocalState = () => {
    setUploadFile(null);
    setTooltipOpen(false);
    setSuccessMessage(false);
    setTestConnectionStatus("");
    setChecked(false);
    setSelectedNdevice("");
    setStreamUrlNew("");
    setFormData(getInitialFormData());
    setRenderDeviceType({});
  };

  const handleClose = () => {
    setOpen(false);
    setActionButton("");
    handleCloseFromParent();
    resetLocalState();
    setFormError("");
    dispatch(resetDeviceState());
    dispatch({ type: "UPLOAD_LIST_ERROR_FILE" });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const decodedToken = jwtDecode(token);

      const formDataToSend = buildUpdateDevicePayload({
        formData,
        editData,
        userGuid: decodedToken.userGuid,
        checked,
      });

      dispatch(updateDevice(editData.id, formDataToSend));
      dispatch({ type: "DEVICE_DUPLICATE_RESET" });
    } catch (error) {
      // console.error("Error updating device:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (actionButton !== "bulkupload" && !runValidation()) return;

    if (actionButton === "add") {
      handleAdd(e);
    } else if (actionButton === "edit") {
      handleEdit(e);
    } else if (actionButton === "bulkupload") {
      if (uploadFile) {
        const validTypes = [
          "application/vnd.ms-excel", // .xls
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        ];

        if (!validTypes.includes(uploadFile.type)) {
          setFormError("Only .xls or .xlsx files are allowed.");
          return;
        }
        setFormError("");
        dispatch(uploadActions(uploadFile));
      } else {
        setFormError("Please upload the file.");
      }
    }
    dispatch({ type: "UPLOAD_LIST_ERROR_FILE" });
  };

  const handleTestConnection = (e) => {
    e.preventDefault();
    const url = streamUrlNew;

    const rtspRegex = /^rtsp:\/\/.+/;

    if (!url || !rtspRegex.test(url)) {
      setTestConnectionStatus("error");
      console.error("Invalid URL: Please enter a valid RTSP stream URL.");
      return;
    }

    setTestConnectionStatus("success");
    console.log("RTSP URL is valid.");
  };

  useEffect(() => {
    dispatch(
      subscribeMQTT(process.env.REACT_APP_DEVICES_LISTEN, (data) => {
            //  toggleCheckbox(data.topic, data.message);
            toggleCheckbox()
        console.log("Received topic:", data);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        if (tooltipOpen) {
          setTooltipOpen(false);
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    if (open) {
      window.addEventListener("keydown", onKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, tooltipOpen]);

  useEffect(() => {
    if (messages.length > 0 && mqtt.connected) {
      let result = {};
      messages.forEach(({ topic, message }) => {
        try {
          if (message === "true" || message === "false") return;

          const parsed = JSON.parse(message);
          const [key, value] = Object.entries(parsed)[0];

          if (value?.topic?.includes("pushbutton")) {
            result[value.topic] = value.name;
          }
        } catch (err) {
          // console.error(`Failed to parse message for topic ${topic}:`, err);
        }
        setMapWithNiagraList(result);
      });
    }
  }, [messages, mqtt]);

  const toggleCheckbox = () => {
    setChecked((prevChecked) => !prevChecked);
  };

  const handleSelect = (event, selectedDevice) => {
    if (selectedDevice) {
      const topic =
        Object.keys(mapWithNiagraList).find(
          (key) => mapWithNiagraList[key] === selectedDevice
        ) || "";

      setSelectedNdevice(selectedDevice);
      setFormData((prevData) => ({
        ...prevData,
        topicName: topic,
      }));
    }
  };

  const options =
    mapWithNiagraList && Object.keys(mapWithNiagraList).length > 0
      ? Object.values(mapWithNiagraList)
      : [];

  const connectionClass = getConnectionClass(testConnectionStatus);

  useEffect(() => {
    dispatch(fetchDeviceTypes());
    dispatch(fetchStatusTypes());
  }, [dispatch]);

  useEffect(() => {
    if (actionButton === "edit" && editData) {
      const editFormData = getEditFormDataFromEditData(editData);
      setFormData(editFormData);
      setChecked(!!editData.isNiagaraMapped);
      setSelectedNdevice(editData.niagaraDeviceName);
      setRenderDeviceType(editData.type || {});
      setStreamUrlNew(editData.streamUrl || "");
    } else if (actionButton === "add") {
      setFormData(getInitialFormData());
      setRenderDeviceType({});
      setChecked(false);
      setSelectedNdevice("");
      setStreamUrlNew("");
    }
    if (editData && editData.niagaraDeviceName) {
      setChecked(true);
    }
  }, [actionButton, editData]);

  useEffect(() => {
    dispatch(fetchTriggerOptions());
  }, [dispatch]);

  useEffect(() => {
    if (
      addDeviceState.isSuccess ||
      updateDeviceState.isSuccess ||
      bulkupload?.success
    ) {
      setSuccessMessage(true);
    }
  }, [
    addDeviceState.isSuccess,
    updateDeviceState.isSuccess,
    bulkupload?.success,
  ]);

  useEffect(() => {
    if (open) {
      dispatch(fetchDeviceTypes());
    }
  }, [open, dispatch]);

  const renderInputField = (data) => (
    <TextField
      id={data.id}
      name={data.name}
      label={data.label}
      variant="outlined"
      onChange={handleInputChange}
      value={formData[data.name] || ""}
      error={!!formError?.[data.name]}
      className="innerFields addDevices"
      helperText={formError?.[data.name] || data.helperText}
      fullWidth
      slots={{ input: "input" }}
      slotProps={{
        input: { style: { borderRadius: "4px", border: "1px solid" } },
      }}
    />
  );

  const renderSelectField = (data) => (
    <FormControl
      fullWidth
      className={`innerFields addDevices ${
        data.label === "Trigger By" ? "trigerbyWrapper" : " "
      }`}
    >
      <InputLabel
        variant="standard"
        htmlFor={data.id}
        shrink={false}
        className="dropDownLabel"
      >
        {data.label}
      </InputLabel>
      <ComboBox
        label="Device Type"
        options={(data.name === "status" ? statusType : deviceType)
          ?.sort((a, b) => a.name.localeCompare(b.name))
          .map((item) => item.name)}
        value={
          data.name === "status"
            ? formData.status || ""
            : formData.type?.name || ""
        }
        onChange={(e) =>
          data.name === "status"
            ? handleStatusTypeChange(e)
            : handleDeviceTypeChange(e)
        }
        classes="customCombo"
      />
      <FormHelperText>
        {data.name === "deviceType" ? formError?.type : ""}
      </FormHelperText>
    </FormControl>
  );

  const renderMultiSelect = (data) => {
    const selectedTriggerIds = new Set(formData.triggers);

    const selectedOptions = Array.isArray(triggerOption)
      ? triggerOption.filter((option) => selectedTriggerIds.has(option.id))
      : [];
    return (
      <FormControl fullWidth className="innerFields addDevices triggerFeild">
        <InputLabel
          variant="standard"
          htmlFor={data.id}
          shrink={false}
          className="dropDownLabel"
        >
          {data.label}
        </InputLabel>
        <ComboBoxMultiSelect
          label="Select Users"
          options={triggerOption?.map((trigger) => ({
            key: trigger.id,
            value: trigger.name,
          }))}
          value={selectedOptions?.map(
            (selectedOption) => selectedOption.id
          )}
          handleChange={(value) => handleTriggersId(value)}
        />
      </FormControl>
    );
  };

  const renderDatePicker = (data) => (
    <div className="innerFields dateField">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={data.label}
          value={formData.dateInstalled}
          onChange={handleDateChange}
          className="date-installed-field "
          renderInput={(params) => (
            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor={params.inputProps.id}
                shrink={false}
                className="dropDownLabel"
              >
                {data.label}
              </InputLabel>
              <TextField
                {...params}
                fullWidth
                slotProps={{
                  textField: {
                    error: !!formError?.dateInstalled,
                    helperText: formError?.dateInstalled,
                  },
                }}
                variant="outlined"
                shrink={false}
              />
            </FormControl>
          )}
        />
        {formError?.dateInstalled && (
          <FormHelperText>Please enter installation date</FormHelperText>
        )}
      </LocalizationProvider>
    </div>
  );

  const renderFields = (data) => {
    debugger;
    switch (data.type) {
      case "Camera": {
              // ✅ Handle case when formData.type.id === 5
        if (formData.type?.id === 5) {
          if (data.id === "streamUrl" || data.id === "hlsUrl") {
            return (
              <div className="cameraCase">
                <TextField
                  id={data.id}
                  name={data.name}
                  label={data.label}
                  variant="outlined"
                  onChange={handleInputChange}
                  value={formData[data.name] || ""}
                  error={!!formError[data.name]}
                  className="innerFields addDevices"
                  helperText={formError[data.name] || data.helperText}
                  fullWidth
                  slots={{ input: "input" }}
                  slotProps={{
                    input: { style: { borderRadius: "4px", border: "1px solid" } },
                    "data-testid": data.id,
                  }}
                />
                {data.id === "streamUrl" && (
                  <button
                    className={`testConnection ${connectionClass}`}
                    onClick={(e) => {
                      handleTestConnection(e);
                    }}
                    data-testid="test-connection-button"
                  >
                    Test Connection
                  </button>
                )}
              </div>
            );
          }
          return null; // Only render streamUrl and hlsUrl
        }
        const isCameraType =
          (renderDeviceType.name === "Camera" &&
            renderDeviceType.name !== "") ||
          formData.type?.id === 1;
        return isCameraType ? (
          <div
            className={`${
              renderDeviceType.name === "Camera" ? "cameraCase" : ""
            }`}
          >
            <TextField
              id={data.id}
              name={data.name}
              label={data.label}
              data-testid={`input-${data.name}`}
              variant="outlined"
              onChange={handleInputChange}
              value={formData[data.name] || ""}
              error={!!formError?.[data.name]}
              className="innerFields addDevices"
              helperText={formError?.[data.name] || data.helperText}
              fullWidth
              slots={{ input: "input" }}
              slotProps={{
                input: {
                  style: { borderRadius: "4px", border: "1px solid" },
                },
              }}
            />
            {data.id === "streamUrl" && (
              <button
                className={`testConnection ${connectionClass}`}
                onClick={handleTestConnection}
                data-testid="test-connection-button"
              >
                Test Connection
              </button>
            )}
          </div>
        ) : null;
      }
      case "input": {
        if (actionButton === "edit" && data.name === "topicName") {
          return (
            <TextField
              id={data.id}
              name={data.name}
              label={data.label}
              variant="outlined"
              onChange={handleInputChange}
              value={getFormattedValue(formData, data?.name)}
              error={!!formError?.[data.name]}
              className={`innerFields addDevices ${
                checked ? "custom-disabled" : ""
              }`}
              helperText={formError?.[data.name] || data.helperText}
              disabled={checked}
              fullWidth
              slots={{ input: "input" }}
              slotProps={{
                input: {
                  style: { borderRadius: "4px", border: "1px solid" },
                },
              }}
            />
          );
        }
        return (data.name !== "topicName" &&
          data.name !== "niagaraDeviceName") ||
          (data.name === "niagaraDeviceName" && checked)
          ? renderInputField(data)
          : null;
      }
      case "select":
        return renderSelectField(data);
      case "multiSelect":
        return actionButton === "edit" ? renderMultiSelect(data) : null;
      case "datePicker":
        return renderDatePicker(data);
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={tooltipOpen}
      className={`globalForm addDeviceEntry`}
    >
      <div role="none">
        <span
          aria-live="polite"
          className="sr-only"
          data-testid="niagaraDeviceAdded"
        >
          {formError && Object.values(formError).filter(Boolean).join(", ")}
        </span>
        {actionButton === "bulkupload" ? (
          <Box
            role="dialog"
            aria-labelledby="bulkupload-device-title"
            aria-modal="true"
            sx={style}
            className="mainModalBox"
          >
            <div className="Modal_header">
              <h1 id="bulkupload-device-title">
                {bulkuploadListFail?.errorFile?.length > 0
                  ? "Bulk Upload Failed"
                  : "Bulk Upload"}
              </h1>
              <AccessibleTooltip title="Close">
                <button
                  ref={firstInputRefClose}
                  data-testid="close-icon"
                  onClick={() => handleClose()}
                  onFocus={() => setTooltipOpen(true)}
                  onBlur={() => setTooltipOpen(false)}
                  onMouseEnter={() => setTooltipOpen(true)}
                  onMouseLeave={() => setTooltipOpen(false)}
                  className={"close_button_modal"}
                >
                  <img src={closeIcon} alt="" aria-hidden="true" />
                </button>
              </AccessibleTooltip>
            </div>
            <div
              className="Modal_middle"
              style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {successMessage ? (
                <div
                  role="alert"
                  aria-live="polite"
                  className="emailsent-wrapper manageUser_details"
                >
                  <img src={checkImg} alt="" aria-hidden="true" />
                  <h2>Success</h2>
                  {actionButton !== "bulkupload" ? (
                    <p>
                      Device has{" "}
                      {actionButton.toString() === "add"
                        ? "created"
                        : "updated"}{" "}
                      successfully.
                    </p>
                  ) : (
                    <p>Bulk upload successful.</p>
                  )}
                </div>
              ) : bulkuploadListFail?.errorFile?.length > 0 ? (
                <div className="fileErrorDownloadmodal">
                  <p>
                    Bulk upload failed as some records have errors. Please check
                    the attached file, fix the errors, and try again.
                  </p>
                  <div className="Modal_footer">
                    <Box sx={{ display: "flex" }}>
                      <Button
                        variant="contained"
                        className="saveBtn"
                        data-testid="error-file-button"
                        onClick={() =>
                          downloadErrorCSV(bulkuploadListFail.errorFile)
                        }
                        ref={downloadErrorBtn}
                      >
                        <img
                          src={downloadFile}
                          alt=""
                          aria-hidden="true"
                        />
                        Download Error File
                      </Button>
                    </Box>
                  </div>
                </div>
              ) : (
                <>
                  <div className="uploadFileMiddle">
                    <div
                      className="innerFields attachmentField"
                      aria-describedby="fileSizetext mexsixetext"
                    >
                      <label
                        htmlFor="bulkUploadInput"
                        role="button"
                        tabIndex={0}
                        className="attachmentBtn"
                        aria-label="Browse file from your computer"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            document
                              .getElementById("bulkUploadInput")
                              .click();
                          }
                        }}
                      >
                        <img
                          src={uploadFileImg}
                          alt=""
                          aria-hidden="true"
                        />
                      </label>

                      <input
                        id="bulkUploadInput"
                        type="file"
                        accept=".xlsx,.xls"
                        data-testid="bulkUploadInput"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setUploadFile(file);
                          }
                        }}
                      />
                    </div>

                    <p className="inst" aria-hidden="true">
                      Browse from your computer
                    </p>
                    <p className="type" id="fileSizetext">
                      (File type: .XLSX or .XLS)
                    </p>
                    <p className="type" id="mexsixetext">
                      Max Size: 5MB
                    </p>
                  </div>
                  {uploadFile?.name}
                  {actionButton === "bulkupload" && (
                    <FormHelperText
                      error
                      style={{ textAlign: "center" }}
                    >
                      {formError}
                    </FormHelperText>
                  )}
                  <div className="Modal_footer">
                    <Box sx={{ display: "flex" }}>
                      <Button
                        variant="contained"
                        className="saveBtn"
                        data-testid="save-button"
                        onClick={(e) => handleSubmit(e)}
                      >
                        Save
                      </Button>
                    </Box>
                  </div>
                </>
              )}
            </div>
          </Box>
        ) : (
          <Box
            role="dialog"
            aria-labelledby="add-edit-device-title"
            aria-modal="true"
            sx={style}
            className="mainModalBox"
          >
            <div className="Modal_header">
              <h1 id="add-edit-device-title">
                {actionButton === "edit" ? "Edit Device" : "Add Device"}
              </h1>
              <AccessibleTooltip title="Close">
                <button
                  ref={firstInputRefClose}
                  onClick={() => handleClose()}
                  data-testid="close-icon"
                  onFocus={() => setTooltipOpen(true)}
                  onBlur={() => setTooltipOpen(false)}
                  onMouseEnter={() => setTooltipOpen(true)}
                  onMouseLeave={() => setTooltipOpen(false)}
                  className={"close_button_modal"}
                >
                  <img src={closeIcon} alt="" aria-hidden="true" />
                </button>
              </AccessibleTooltip>
            </div>
            <div className="Modal_middle">
              {!successMessage ? (
                <Box
                  component="form"
                  noValidate
                  autoComplete="off"
                  className=""
                >
                  {actionButton.toString() === "edit" &&
                    formData.type.id === 4 && (
                      <div className="mapNigraCheck">
                        <label htmlFor="niagaraCheckbox">
                          Map with Niagara
                        </label>
                        <Checkbox
                          id="niagaraCheckbox"
                          checked={checked}
                          onChange={toggleCheckbox}
                          className="checkboxNiagra"
                        />
                      </div>
                    )}
                  {field.map((data) => renderFields(data))}
                </Box>
              ) : (
                <div
                  role="alert"
                  aria-live="polite"
                  className="emailsent-wrapper manageUser_details"
                >
                  <img src={checkImg} alt="" aria-hidden="true" />
                  <h2>Success</h2>
                  {actionButton !== "bulkupload" ? (
                    <p>
                      Device has{" "}
                      {actionButton.toString() === "add"
                        ? "created"
                        : "updated"}{" "}
                      successfully.
                    </p>
                  ) : (
                    <p>Bulk upload successful.</p>
                  )}
                </div>
              )}
            </div>
            <div className="Modal_footer">
              {!successMessage && (
                <Box sx={{ display: "flex" }}>
                  <Button
                    variant="contained"
                    className="saveBtn"
                    data-testid="save-button"
                    onClick={(e) => handleSubmit(e)}
                  >
                    Save
                  </Button>
                </Box>
              )}
            </div>
          </Box>
        )}
      </div>
    </Modal>
  );
};

AddEditDevice.propTypes = {
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  formError: PropTypes.object,
  setFormError: PropTypes.func.isRequired,
  actionButton: PropTypes.string.isRequired,
  setActionButton: PropTypes.func.isRequired,
  editData: PropTypes.object,
  rows: PropTypes.array.isRequired,
  existingDeviceNames: PropTypes.arrayOf(PropTypes.string),
  handleClose: PropTypes.func,
};

export default AddEditDevice;
